// navConfig — single source of truth for the authenticated sidebar (nav-ia-003).
//
// This is plain data (item objects + an ordered group registry) so the
// visibility logic can be unit-tested by feeding synthetic `user` objects
// (see nav-ia-006). Icons are imported component references, not JSX.
//
// IMPORTANT: nav is a visibility layer, not the access boundary (routes are
// separately guarded in App.js + backend require_* gates), so grouping cannot
// widen access. Items removed here keep their routes and role guards.
//
// 2026-07-20 reorg (STARSEED-CAPMARKETS-052, per Jeff's 2026-07-18 direction):
//   - Admin surfaces consolidate under one "Command Center" group (first admin
//     group): pipeline/CRM, VC# registry, pioneer codes, finance, announcements,
//     compliance & security.
//   - De-listed from the sidebar: Lifestyle, Lifestyle Admin, Mailbox Setup,
//     Analytics (moves to the FINBPM portal — ticket 058), admin Deal Room
//     (duplicate of the member Deal Room), Fund Tracking / Partner Account /
//     How to Invest (now reached from inside the Deal Room hub).
//     PocketOption, Comp Fund, Commissions, Introducer Payouts, and Trial
//     Reviews were later removed outright — routes and pages deleted
//     (STARSEED-CAPMARKETS-080, attorney direction).
//   - Account group sits directly under the Dashboard.
// Naming fixes from the PO decision (2026-06-14) are retained:
//   - member /partner-funding  → "Funding Status"  (never "Funding Instructions")
//   - admin  /funding          → "Funding Queue"

import {
  Calculator,
  LayoutDashboard,
  Users,
  FileCheck,
  Settings,
  CreditCard,
  Target,
  Banknote,
  Upload,
  Megaphone,
  ShieldCheck,
  Mail,
  ShieldQuestion,
  Hash,
  Network,
  Activity,
  Briefcase,
  Anchor,
  Link2,
  Receipt,
} from "lucide-react";

const ALL = ["admin", "superuser", "client", "referrer"];
const ADMIN = ["admin", "superuser"];

// Ordered group registry. `defaultCollapsed` is honored by the sidebar's
// collapse scaffold; all groups ship expanded for now.
export const NAV_GROUPS = [
  { key: "overview", label: "Overview", order: 10, defaultCollapsed: false },
  { key: "account", label: "Account", order: 20, defaultCollapsed: false },
  { key: "get_started", label: "Get Started", order: 30, defaultCollapsed: false },
  { key: "invest", label: "Invest", order: 40, defaultCollapsed: false },
  // "Earn" renamed to "Network" (STARSEED-CAPMARKETS-081) — key kept stable
  // so persisted collapse state and tests survive the label change.
  { key: "earn", label: "Network", order: 50, defaultCollapsed: false },
  { key: "admin_command_center", label: "Command Center", order: 60, defaultCollapsed: false },
  { key: "admin_members", label: "Members & Access", order: 70, defaultCollapsed: false },
  { key: "admin_settings", label: "Settings", order: 80, defaultCollapsed: false },
];

// Nav items. `badgeKey` items receive a live count injected by the Sidebar:
// "unread" (Messages) for everyone; "kyc_actions" (members awaiting a KYC /
// accreditation approval, STARSEED-CAPMARKETS-071) fetched for admins only.
// Order within a group follows array order.
// `testId` overrides the label-derived test-id slug — required where two items
// share a label (member vs admin Command Center) so admins, who see both,
// don't get colliding `data-testid="nav-..."` values.
export const NAV_ITEMS = [
  // Overview
  { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ALL, group: "overview" },

  // Account — directly under the Dashboard
  { href: "/payments", label: "Payments", icon: CreditCard, roles: ALL, group: "account" },
  // STARSEED-CAPMARKETS-219 — partially reverses the 2026-07-20 de-listing
  // above. Partner Account left the sidebar when it was a read-only FINBPM
  // portal reachable from the Deal Room hub. It now also carries the
  // member's own cash movements and the ledger-derived estimate, and the
  // dashboard disclosure tells them to come here — so it needs a path that
  // does not depend on knowing the Deal Room contains it. Jamie Fischer,
  // 2026-08-28: "I don't know how to go into any tabs as I don't have
  // those on my portal." They were right; there was no such tab.
  //
  // `module: "monaxa"` MIRRORS App.js's `<ProtectedRoute requiredModule=
  // "monaxa">` on this route. The first version of this fix deliberately
  // omitted the gate to guarantee reachability — which produced a link that
  // bounces to /dashboard for any member with the module explicitly
  // disabled. A visible link the router refuses is worse than no link: it
  // reads as a broken portal rather than an absent feature. Nav must not
  // advertise what the route will reject; `nav module ↔ route requiredModule`
  // parity is asserted in navConfig.test.js.
  //
  // NOTE: for a member whose monaxa is false, this is a DATA fix, not a code
  // one — they cannot load the page under any nav configuration.
  { href: "/partner-account", label: "Transactions", icon: Receipt, roles: ALL, group: "account", module: "monaxa" },
  { href: "/messages", label: "Messages", icon: Mail, roles: ALL, group: "account", badgeKey: "unread" },
  { href: "/kyc", label: "Documents", icon: FileCheck, roles: ALL, group: "account" },

  // Get Started — onboarding task-flow
  { href: "/command-center", label: "Command Center", icon: ShieldCheck, roles: ALL, group: "get_started" },
  { href: "/kyc-verification", label: "Verification & Accreditation", icon: ShieldQuestion, roles: ALL, group: "get_started" },
  { href: "/partner-funding", label: "Funding Status", icon: Banknote, roles: ["client", "referrer"], group: "get_started" },

  // Invest — the Deal Room is the hub; How to Invest, Fund Tracking, and the
  // Partner Account are linked from inside it (STARSEED-CAPMARKETS-052/058).
  { href: "/dealflow", label: "Deal Room", icon: Briefcase, roles: ALL, group: "invest" },

  // Network — referral / introducer
  { href: "/introducer-program", label: "Introducer Program", icon: Network, roles: ALL, group: "earn" },

  // Admin — Command Center (consolidated admin operations)
  { href: "/admin/command-center", label: "Command Center", icon: Activity, roles: ADMIN, group: "admin_command_center", testId: "admin-command-center", badgeKey: "kyc_actions" },
  { href: "/admin/investor-files", label: "Investor File", icon: ShieldCheck, roles: ADMIN, group: "admin_command_center" },
  { href: "/compliance-pipeline", label: "Compliance Pipeline", icon: Activity, roles: ADMIN, group: "admin_command_center" },
  { href: "/lead-management", label: "Lead Pipeline", icon: Target, roles: ADMIN, group: "admin_command_center" },
  { href: "/import-contacts", label: "Import Contacts", icon: Upload, roles: ADMIN, group: "admin_command_center" },
  { href: "/admin/vc-registry", label: "VC# Registry", icon: Hash, roles: ADMIN, group: "admin_command_center" },
  { href: "/admin/promo-codes", label: "Pioneer Codes", icon: Anchor, roles: ADMIN, group: "admin_command_center" },
  { href: "/funding", label: "Funding Queue", icon: Banknote, roles: ADMIN, group: "admin_command_center" },
  // STARSEED-CAPMARKETS-134 — the unresolved partner-account queue. An account
  // sitting here is a member whose money is missing from their dashboard, so it
  // belongs next to the other things an admin is expected to clear, not buried
  // in settings.
  { href: "/admin/partner-accounts", label: "Partner Accounts", icon: Link2, roles: ADMIN, group: "admin_command_center" },
  // STARSEED-CAPMARKETS-194 — balance entry sits next to the mapping queue
  // because they are two halves of one job: an account is attached here, and
  // its figures arrive there.
  { href: "/admin/balance-entry", label: "Enter Balances", icon: Calculator, roles: ADMIN, group: "admin_command_center" },
  { href: "/membership-fees", label: "Membership Fees", icon: CreditCard, roles: ADMIN, group: "admin_command_center" },
  { href: "/admin/stripe/preflight", label: "Stripe Preflight", icon: ShieldCheck, roles: ADMIN, group: "admin_command_center" },
  { href: "/announcements", label: "Announcements", icon: Megaphone, roles: ADMIN, group: "admin_command_center" },
  { href: "/verification", label: "Verification Portal", icon: ShieldQuestion, roles: ADMIN, group: "admin_command_center" },
  { href: "/admin/security-status", label: "Security Posture", icon: ShieldCheck, roles: ADMIN, group: "admin_command_center" },

  // Admin — Members & Access
  { href: "/users", label: "User Management", icon: Users, roles: ADMIN, group: "admin_members" },
  { href: "/admin/users/invite", label: "User Invites", icon: Mail, roles: ADMIN, group: "admin_members" },
  { href: "/admin/activity-log", label: "Member Activity Log", icon: Activity, roles: ADMIN, group: "admin_members" },

  // Admin — Settings
  { href: "/settings", label: "Settings", icon: Settings, roles: ADMIN, group: "admin_settings" },
];

// Pure visibility filter — identical semantics to the pre-reorg list.
// Exported for unit testing (nav-ia-006).
//   - role must be listed on the item
//   - admin/superuser see every module link (role override)
//   - non-admins: a `module` link is hidden only when explicitly disabled
//     (module_access[key] === false)
export function visibleNavItems(user) {
  const role = user?.role;
  const isPrivileged = role === "admin" || role === "superuser";
  const modAccess = user?.module_access || {};
  return NAV_ITEMS.filter((item) => {
    if (!item.roles.includes(role)) return false;
    if (isPrivileged) return true;
    if (item.module && modAccess[item.module] === false) return false;
    return true;
  });
}

// Group an already-filtered item list into ordered, non-empty sections.
// Exported for unit testing (nav-ia-006).
export function groupVisibleItems(items) {
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: items.filter((item) => item.group === group.key),
  })).filter((group) => group.items.length > 0);
}
